import type { RefreshTagsUpdatedEnvelope } from "./topics";

type HeadersLike =
  | Headers
  | Response
  | { get: (name: string) => string | null }
  | { getResponseHeader: (name: string) => string | null };

export type RealtimeRefreshHandlerContext = {
  envelope?: RefreshTagsUpdatedEnvelope;
  currentVersion: number;
  incomingVersion: number;
  gapDetected: boolean;
  setVersion: (version: number) => void;
  syncFromHeaders: (headersLike: HeadersLike) => void;
};

export type RealtimeRefreshHandler = (
  node: HTMLElement,
  context: RealtimeRefreshHandlerContext,
) => void | Promise<void>;

type RealtimeHeaderVersion = {
  subjectKey: string;
  tag: string;
  version: number;
};

declare global {
  interface Window {
    __WMU_REALTIME_REFRESH_BOUND?: boolean;
  }
}

const refreshHandlerRegistry = new Map<string, RealtimeRefreshHandler>();

function readHeader(headersLike: HeadersLike | null | undefined, name: string): string {
  if (!headersLike) {
    return "";
  }
  if (headersLike instanceof Response) {
    return String(headersLike.headers.get(name) || "").trim();
  }
  if (headersLike instanceof Headers) {
    return String(headersLike.get(name) || "").trim();
  }
  if ("get" in headersLike && typeof headersLike.get === "function") {
    return String(headersLike.get(name) || "").trim();
  }
  if ("getResponseHeader" in headersLike && typeof headersLike.getResponseHeader === "function") {
    return String(headersLike.getResponseHeader(name) || "").trim();
  }
  return "";
}

function parseVersion(raw: string | null | undefined): number {
  const parsed = Number(String(raw || "").trim());
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.floor(parsed);
}

function getNodeSubjectKey(node: HTMLElement): string {
  return String(node.getAttribute("data-realtime-subject-key") || "").trim();
}

function getNodeTag(node: HTMLElement): string {
  return String(node.getAttribute("data-realtime-tag") || "").trim();
}

function getNodeVersion(node: HTMLElement): number {
  return parseVersion(node.getAttribute("data-realtime-version"));
}

function setNodeVersion(node: HTMLElement, version: number): void {
  node.setAttribute("data-realtime-version", String(Math.max(0, Math.floor(version))));
}

function getPendingVersion(node: HTMLElement): number {
  return parseVersion(node.getAttribute("data-realtime-pending-version"));
}

function findRealtimeTargets(subjectKey: string, tag: string): HTMLElement[] {
  const matchSubjectKey = String(subjectKey || "").trim();
  const matchTag = String(tag || "").trim();
  if (!matchSubjectKey || !matchTag) {
    return [];
  }
  return Array.from(document.querySelectorAll<HTMLElement>("[data-realtime-subject-key][data-realtime-tag]"))
    .filter((node) => getNodeSubjectKey(node) === matchSubjectKey && getNodeTag(node) === matchTag);
}

function parseRealtimeHeaderVersion(headersLike: HeadersLike | null | undefined): RealtimeHeaderVersion | null {
  const subjectKey = readHeader(headersLike, "X-WM-Realtime-Subject-Key");
  const tag = readHeader(headersLike, "X-WM-Realtime-Refresh-Tag");
  const version = parseVersion(readHeader(headersLike, "X-WM-Realtime-Refresh-Version"));
  if (!subjectKey || !tag) {
    return null;
  }
  return { subjectKey, tag, version };
}

function completeRealtimeRefresh(node: HTMLElement, headersLike?: HeadersLike): void {
  const parsed = syncRealtimeVersionsFromHeaders(headersLike);
  const desiredVersion = getPendingVersion(node);
  let currentVersion = getNodeVersion(node);
  if (!parsed && desiredVersion > currentVersion) {
    setNodeVersion(node, desiredVersion);
    currentVersion = desiredVersion;
  }
  if (currentVersion < desiredVersion) {
    node.removeAttribute("data-realtime-pending");
    triggerRealtimeRefresh(node, desiredVersion);
    return;
  }
  node.removeAttribute("data-realtime-pending");
  node.removeAttribute("data-realtime-pending-version");
}

function triggerRealtimeRefresh(node: HTMLElement, incomingVersion: number, envelope?: RefreshTagsUpdatedEnvelope): void {
  const handlerName = String(node.getAttribute("data-realtime-handler") || "").trim();
  const refreshURL = String(
    node.getAttribute("data-realtime-refresh-url")
      || node.getAttribute("data-refresh-url")
      || node.getAttribute("hx-get")
      || "",
  ).trim();
  const currentVersion = getNodeVersion(node);
  const context: RealtimeRefreshHandlerContext = {
    envelope,
    currentVersion,
    incomingVersion,
    gapDetected: incomingVersion > currentVersion + 1,
    setVersion: (version: number) => {
      setNodeVersion(node, version);
    },
    syncFromHeaders: (headersLike: HeadersLike) => {
      syncRealtimeVersionsFromHeaders(headersLike);
    },
  };

  const handler = handlerName ? refreshHandlerRegistry.get(handlerName) : undefined;
  if (handler) {
    void Promise.resolve(handler(node, context))
      .then(() => {
        completeRealtimeRefresh(node);
      })
      .catch(() => {
        node.removeAttribute("data-realtime-pending");
      });
    return;
  }

  if (!refreshURL || !window.htmx) {
    completeRealtimeRefresh(node);
    return;
  }

  const target = node.id ? `#${node.id}` : node;
  const swap = String(node.getAttribute("data-refresh-swap") || node.getAttribute("hx-swap") || "innerHTML").trim() || "innerHTML";
  window.htmx.ajax("GET", refreshURL, { target, swap });
}

function queueRealtimeRefresh(node: HTMLElement, incomingVersion: number, envelope?: RefreshTagsUpdatedEnvelope): void {
  const currentVersion = getNodeVersion(node);
  const pendingVersion = getPendingVersion(node);
  const desiredVersion = Math.max(incomingVersion, pendingVersion);
  if (desiredVersion <= currentVersion) {
    return;
  }
  node.setAttribute("data-realtime-pending-version", String(desiredVersion));
  if (node.getAttribute("data-realtime-pending") === "true") {
    return;
  }
  node.setAttribute("data-realtime-pending", "true");
  triggerRealtimeRefresh(node, desiredVersion, envelope);
}

function bindHTMXRealtimeRefreshSync(): void {
  if (window.__WMU_REALTIME_REFRESH_BOUND) {
    return;
  }
  window.__WMU_REALTIME_REFRESH_BOUND = true;

  document.addEventListener("htmx:afterRequest", (event: Event) => {
    const detail = (event as CustomEvent<{ xhr?: XMLHttpRequest; target?: EventTarget; elt?: EventTarget }>).detail;
    const target = detail?.target instanceof HTMLElement
      ? detail.target
      : detail?.elt instanceof HTMLElement
        ? detail.elt
        : null;
    if (!(target instanceof HTMLElement)) {
      syncRealtimeVersionsFromHeaders(detail?.xhr);
      return;
    }
    completeRealtimeRefresh(target, detail?.xhr);
  });

  document.addEventListener("htmx:responseError", (event: Event) => {
    const detail = (event as CustomEvent<{ target?: EventTarget; elt?: EventTarget }>).detail;
    const target = detail?.target instanceof HTMLElement
      ? detail.target
      : detail?.elt instanceof HTMLElement
        ? detail.elt
        : null;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    target.removeAttribute("data-realtime-pending");
  });
}

export function registerRealtimeRefreshHandler(name: string, handler: RealtimeRefreshHandler): () => void {
  const key = String(name || "").trim();
  if (!key) {
    return () => undefined;
  }
  bindHTMXRealtimeRefreshSync();
  refreshHandlerRegistry.set(key, handler);
  return () => {
    if (refreshHandlerRegistry.get(key) === handler) {
      refreshHandlerRegistry.delete(key);
    }
  };
}

export function hasRealtimeRefreshTarget(subjectKey: string, tag: string): boolean {
  return findRealtimeTargets(subjectKey, tag).length > 0;
}

export function syncRealtimeVersionsFromHeaders(headersLike?: HeadersLike): RealtimeHeaderVersion | null {
  const parsed = parseRealtimeHeaderVersion(headersLike);
  if (!parsed) {
    return null;
  }
  findRealtimeTargets(parsed.subjectKey, parsed.tag).forEach((node) => {
    setNodeVersion(node, Math.max(getNodeVersion(node), parsed.version));
  });
  return parsed;
}

export function handleRealtimeRefreshEnvelope(envelope: RefreshTagsUpdatedEnvelope): void {
  bindHTMXRealtimeRefreshSync();
  const subjectKey = String(envelope?.payload?.subject_key || "").trim();
  if (!subjectKey || !Array.isArray(envelope?.payload?.versions)) {
    return;
  }
  envelope.payload.versions.forEach((entry) => {
    const tag = String(entry?.tag || "").trim();
    const version = parseVersion(String(entry?.version ?? ""));
    if (!tag || version <= 0) {
      return;
    }
    findRealtimeTargets(subjectKey, tag).forEach((node) => {
      queueRealtimeRefresh(node, version, envelope);
    });
  });
}
